import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { LoadingIndicator } from "../../components/LoadingIndicator";
import { useSpeechRecognition } from "../../hooks/use-speech-recognition";
import { apiFetch } from "../../lib/api-client";

export const Route = createFileRoute("/_app/objects")({
  component: ObjectsPage,
});

type ProviderChoice = "auto" | "wikimedia" | "openai" | "gemini" | "stability";

type TeachObjectResponse = {
  id: string;
  text: string;
  imageUrl: string;
  provider: "wikimedia" | "openai" | "gemini" | "stability";
};

const teachObject = (input: {
  keyword: string;
  provider: ProviderChoice;
}): Promise<TeachObjectResponse> => {
  const body: Record<string, string> = { keyword: input.keyword };
  if (input.provider !== "auto") {
    body.provider = input.provider;
  }
  return apiFetch<TeachObjectResponse>("/teach/object", {
    method: "POST",
    body: JSON.stringify(body),
  });
};

const providerOptions: Array<{ value: ProviderChoice; label: string }> = [
  { value: "auto", label: "自動 (Wikimedia → AI)" },
  { value: "wikimedia", label: "Wikimedia のみ" },
  { value: "gemini", label: "Gemini" },
  { value: "openai", label: "OpenAI" },
  { value: "stability", label: "Stability" },
];

function ObjectsPage() {
  const speech = useSpeechRecognition();
  const [keyword, setKeyword] = useState("");
  const [provider, setProvider] = useState<ProviderChoice>("auto");

  const mutation = useMutation({
    mutationFn: teachObject,
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const k = (keyword || speech.transcript).trim();
    if (!k) return;
    mutation.mutate({ keyword: k, provider });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">ものの名前</h1>
        <p className="text-sm text-neutral-600 mt-1">
          ことばを話すか、入力してね
        </p>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={keyword || speech.transcript}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="例: りんご"
            className="flex-1 rounded-xl border border-neutral-300 bg-white px-4 py-3 text-lg outline-none focus:border-neutral-900"
          />
          {speech.supported && (
            <button
              type="button"
              onClick={() =>
                speech.listening ? speech.stop() : speech.start()
              }
              className={`rounded-xl px-4 py-3 text-white text-lg transition-colors ${
                speech.listening
                  ? "bg-red-500 hover:bg-red-600"
                  : "bg-neutral-900 hover:bg-neutral-700"
              }`}
              aria-label={speech.listening ? "録音停止" : "音声入力"}
            >
              {speech.listening ? "■" : "🎤"}
            </button>
          )}
        </div>
        {speech.interim && (
          <p className="text-sm text-neutral-500">認識中: {speech.interim}</p>
        )}

        <div className="flex flex-wrap gap-2">
          {providerOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setProvider(opt.value)}
              className={`rounded-full px-3 py-1.5 text-xs border transition-colors ${
                provider === opt.value
                  ? "bg-neutral-900 text-white border-neutral-900"
                  : "bg-white border-neutral-300 hover:border-neutral-500"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <button
          type="submit"
          disabled={mutation.isPending || (!keyword && !speech.transcript)}
          className="w-full rounded-xl bg-neutral-900 py-4 text-white text-lg font-semibold disabled:opacity-50 hover:bg-neutral-700 transition-colors"
        >
          {mutation.isPending ? "つくっています..." : "見せて"}
        </button>
      </form>

      {mutation.isPending && (
        <LoadingIndicator label="つくっています..." variant="bar" />
      )}

      {mutation.isError && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-700">
          {mutation.error.message}
        </div>
      )}

      {mutation.data && (
        <div className="space-y-3">
          <div className="rounded-2xl overflow-hidden border border-neutral-200 bg-white">
            <img
              src={mutation.data.imageUrl}
              alt={mutation.data.text}
              className="w-full h-auto"
            />
          </div>
          <div className="flex items-center justify-between">
            <p className="text-2xl font-bold">{mutation.data.text}</p>
            <span className="text-xs text-neutral-500">
              {mutation.data.provider}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
